import { Box, Grid, IconButton, Typography } from '@mui/material';
import { connect } from 'react-redux';
import { DataGridPro } from '@mui/x-data-grid-pro';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { DATAGRID_LOCALES } from '@commons/utils/dataGrid';
import { ExportButton } from '@commons/ExportButton';
import { getClientInfo } from '@selectors/client';
import { getFormattedCurrencyName } from '@commons/utils/translations';
import { showErrorMessage } from '@actions/messageconfirmation';
import { Tooltip } from '@commons/utils/styledLibraryComponents';

import { fetchProductTimeSeriesData } from './utils/fetch';
import { generateExport } from './utils/export';
import { getMetricOptions } from './utils/config';
import { productColumns } from './utils/column';
import MetricCard from '../common/MetricCard';
import ProductComparisonChart from './ProductComparisonChart';
import ProductTimeSeriesChart from './ProductTimeSeriesChart';

const VIEW_MODES = {
  TABLE: 'table',
  CHART: 'chart',
};

const ProductPanel = ({
  selectedStores,
  currentPeriod,
  previousPeriod,
  showErrorMessage,
  globalData,
  isLoadingGlobal,
  currency,
  client: { storeName },
}) => {
  const metricOptions = getMetricOptions();
  // Product states
  const [isLoading, setIsLoading] = useState(false);
  const [productData, setProductData] = useState({});
  const [productTableData, setProductTableData] = useState([]);
  const [productViewMode, setProductViewMode] = useState(VIEW_MODES.TABLE);
  const [selectedProductForChart, setSelectedProductForChart] = useState(null);
  const [selectedMetricForChart, setSelectedMetricForChart] = useState(metricOptions[1]);
  const [chartData, setChartData] = useState([]);
  const [isLoadingChart, setIsLoadingChart] = useState(false);

  const loadProductData = async () => {
    setIsLoading(true);
    setIsLoadingChart(true);

    try {
      const { productData, productTableData, chartData } = await fetchProductTimeSeriesData(
        selectedStores,
        currentPeriod,
        previousPeriod,
      );

      setProductData(productData);
      setProductTableData(productTableData);
      setChartData(chartData);
    } catch (error) {
      setProductData({});
      setProductTableData([]);
      setChartData([]);
      showErrorMessage('Failed to fetch product data');
    } finally {
      setIsLoading(false);
      setIsLoadingChart(false);
    }
  };

  // Fetch product data and chart data
  useEffect(() => {
    if (
      currentPeriod.startDate &&
      currentPeriod.endDate &&
      previousPeriod.startDate &&
      previousPeriod.endDate &&
      selectedStores.length &&
      !isLoading
    )
      loadProductData();
  }, [
    selectedStores,
    currentPeriod.startDate,
    currentPeriod.endDate,
    previousPeriod.startDate,
    previousPeriod.endDate,
  ]);

  const handleGridClick = (params) => {
    const product = productTableData.find((p) => p.id === params.row.id);
    const metric = metricOptions.find((m) => m.id === params.field);

    if (product) setSelectedProductForChart(product);
    if (metric) {
      setSelectedMetricForChart(metric);
    } else {
      setSelectedMetricForChart(metricOptions[1]);
    }

    setProductViewMode(VIEW_MODES.CHART);
  };

  const handleCellClick = (params, event) => {
    // Prevent the event from bubbling up to trigger onRowClick
    event.stopPropagation();
    handleGridClick(params);
  };

  // Export handler
  const handleExport = () => {
    try {
      generateExport(productTableData, currentPeriod, previousPeriod);
    } catch (error) {
      showErrorMessage(i18next.t('GENERAL.EXPORT_FAILURE'));
    }
  };

  return (
    <Box sx={{ paddingBottom: '64px' }}>
      {/* Metric Cards */}
      <Grid spacing={3} sx={{ mb: 4 }} container>
        <Grid md={4} sm={6} xs={12} item>
          <MetricCard
            currentValue={productData?.units?.current}
            isLoading={isLoading}
            previousValue={productData?.units?.previous}
            title={i18next.t('ANALYSIS.PERFORMANCE.PRODUCT_UNITS_SOLD')}
          />
        </Grid>
        <Grid md={4} sm={6} xs={12} item>
          <MetricCard
            currentValue={productData?.turnoverExcludingTaxes?.current}
            isCurrency={true}
            isLoading={isLoading}
            previousValue={productData?.turnoverExcludingTaxes?.previous}
            title={
              <Box alignItems="center" display="flex">
                {i18next.t('GENERAL.TURNOVER_EX_TAX')}
                <Tooltip
                  text={i18next.t('FORECAST.PAST_MIX.TURNOVER_EX_TAX_TOOLTIP', {
                    currencyName: getFormattedCurrencyName(currency.shortenedName, true),
                  })}
                />
              </Box>
            }
          />
        </Grid>
        <Grid md={4} sm={6} xs={12} item>
          <MetricCard
            currentValue={globalData?.storeCount?.current}
            isLoading={isLoadingGlobal}
            previousValue={globalData?.storeCount?.previous}
            title={i18next.t('ANALYSIS.PERFORMANCE.ACTIVE_STORE_COUNT', { storeName })}
          />
        </Grid>
      </Grid>

      {/* Table/Chart Toggle and Data */}
      <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">{i18next.t('ANALYSIS.PERFORMANCE.PRODUCT_GRID_TITLE')}</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box>
            <IconButton
              sx={{
                width: 42,
                height: 42,
                backgroundColor: productViewMode === 'table' ? 'white' : 'transparent',
                border: productViewMode === 'table' ? '1px solid #E0E0E0' : '1px solid transparent',
              }}
              onClick={() => setProductViewMode(VIEW_MODES.TABLE)}
            >
              <img
                alt="Table"
                src="/images/inpulse/list-black-small.svg"
                style={{ width: 20, height: 20 }}
              />
            </IconButton>
            <IconButton
              sx={{
                width: 42,
                height: 42,
                backgroundColor: productViewMode === 'chart' ? 'white' : 'transparent',
                border: productViewMode === 'chart' ? '1px solid #E0E0E0' : '1px solid transparent',
              }}
              onClick={() => setProductViewMode(VIEW_MODES.CHART)}
            >
              <img
                alt="Chart"
                src="/images/inpulse/forecast-black-small.svg"
                style={{ width: 20, height: 20 }}
              />
            </IconButton>
          </Box>
          <ExportButton
            handleClick={handleExport}
            hasLabel={false}
            isDisabled={isLoading || !productTableData.length}
          />
        </Box>
      </Box>

      {/* Product Table replace by Time Series Chart on toggle*/}
      {productViewMode === VIEW_MODES.TABLE ? (
        <Box sx={{ height: 600, maxHeight: 600, overflow: 'auto' }}>
          <DataGridPro
            columns={productColumns()}
            initialState={{
              sorting: {
                sortModel: [{ field: 'turnoverExcludingTaxes', sort: 'desc' }],
              },
            }}
            loading={isLoading}
            localeText={
              DATAGRID_LOCALES[i18next.language]?.components?.MuiDataGrid?.defaultProps?.localeText
            }
            pageSize={20}
            rows={productTableData}
            sx={{
              '& .MuiCircularProgress-root': {
                color: 'black',
              },
            }}
            disableColumnPinning
            disableColumnReorder
            disableColumnSelector
            disableDensitySelector
            showToolbar
            onCellClick={handleCellClick}
            onRowClick={handleGridClick}
          />
        </Box>
      ) : (
        <ProductTimeSeriesChart
          chartData={chartData}
          currentPeriod={currentPeriod}
          initialSelectedMetric={selectedMetricForChart}
          initialSelectedProduct={selectedProductForChart}
          isLoadingChart={isLoadingChart}
          previousPeriod={previousPeriod}
          productTableData={productTableData}
          selectedStores={selectedStores}
        />
      )}

      {/* Product Comparison Chart */}
      <Box sx={{ mt: 4 }}>
        <ProductComparisonChart
          currentPeriod={currentPeriod}
          isLoading={isLoading}
          previousPeriod={previousPeriod}
          productTableData={productTableData}
          selectedStores={selectedStores}
        />
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  currency: state.baseReducer.currency,
  client: getClientInfo(state.baseReducer.user),
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductPanel);
